// @ts-nocheck
import { flatten, uniq } from 'lodash';
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { ContextualDiscoveryInsightsReportModal } from 'sonata-platform-components';
import { ButtonUI, useQueryFetch } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';

import { getDefaultMessageSelectorKpis, getMetricsKeys } from '@/helpers/reporting.helpers';
import { setContextualDiscoveryReportStatusChanged } from '@/store/actions/reporting.actions';
import {
  getContextualDiscoveryReportStatus,
  getImpressionsKpiChartSelector,
  getSelectedCampaigns
} from '@/store/selectors/reporting.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { getCartographyDictionary } from '@/ts-resources/reporting.resources';
import { GeoLookALikeButton } from './GeoLookALikeButton';
import { ReportingInsightsExporter } from './ReportingInsightsExporter';

import type { ContextualDiscoveryInsightReportInterface } from 'sonata-static-storage/lib/schemas/contextualDiscoveryInsightReport/contextualDiscoveryInsightReport.schema';

type ButtonUIProps = React.ComponentProps<typeof ButtonUI>;

type State = {
  contextualReportModalKey?: string;
};

type Props = {
  onTogglePDFLoading?: (val: boolean) => void;
  sonataButtonProps: ButtonUIProps & {
    customAction: string;
    elementTargetId: string;
    onClick: () => void;
    tooltipReference: string;
  };
};

function DownloadPDFButton(props: Readonly<Props>) {
  const dispatch = useDispatch();
  const { onTogglePDFLoading, sonataButtonProps } = props;
  const user = useSelector(getCurrentUser);
  const userRoles = useSelector(getUserRoles);
  const campaigns = useSelector(getSelectedCampaigns);
  const contextualDiscoveryReportStore = useSelector(getContextualDiscoveryReportStatus);
  const { kpisOptions, metricOptions } = useSelector(getImpressionsKpiChartSelector);

  const contextualDiscoveryInsightsReportModalRef: any = useRef();
  const { formatMessage } = useIntl();
  const { customAction, elementTargetId, onClick, tooltipReference, ...rest } = sonataButtonProps;

  const [{ contextualReportModalKey }, setState] = useState<State>({
    contextualReportModalKey: Math.floor(Math.random() * 0x1000000000).toString(16)
  });

  const hasReportingDownloadV2 = checkUserResourcePermissions('reportingDownloadV2', user, userRoles);

  const formattedMetricsKpis = metricOptions.concat(kpisOptions).map(option => ({
    id: getMetricsKeys(option),
    label: formatMessage({
      defaultMessage: getDefaultMessageSelectorKpis(option) as string,
      id: `reporting.metrics.${option}`
    })
  }));

  const campaignIds = campaigns.map((c: Record<string, any>) => c.id);
  const campaignsCountries: string[] = uniq(flatten(campaigns.map((c: Record<string, any>) => c.countries)));

  const sonataPDFPermission = checkUserResourcePermissions('reportingDownloadPdf', user, userRoles);
  const contextualPDFPermission = checkUserResourcePermissions(
    'reportingDownloadPdfContextualDiscovery',
    user,
    userRoles
  );
  const geoLookALikePermission = checkUserResourcePermissions('geoLookalike', user, userRoles);

  const handleReportModalClosed = () => {
    setState(prevState => ({
      ...prevState,
      contextualReportModalKey: Math.floor(Math.random() * 0x1000000000).toString(16)
    }));
  };

  const handleReportDownloadToggled = (reportData: ContextualDiscoveryInsightReportInterface) => {
    contextualDiscoveryInsightsReportModalRef?.current?.closeModal();
    dispatch(
      setContextualDiscoveryReportStatusChanged({
        name: reportData.name,
        reportId: reportData.reportId,
        reportUrl: '',
        status: reportData.status
      })
    );
  };

  const { data: cartographyDictionaryData, status: cartographyDictionaryStatus } = useQueryFetch<Record<string, any>[]>(
    ['cartographyDictionary', campaignsCountries.join('|')],
    async () => getCartographyDictionary(campaignsCountries)
  );

  let contextualDiscoveryInsightButtonIsEnabled = false;
  if (cartographyDictionaryStatus === 'success' && cartographyDictionaryData && cartographyDictionaryData.length) {
    contextualDiscoveryInsightButtonIsEnabled = cartographyDictionaryData.some(({ tagsConfig }) => {
      return tagsConfig.some(({ discoveryInsightsReports }: { discoveryInsightsReports?: boolean }) => {
        return !!discoveryInsightsReports;
      });
    });
  }

  if (!sonataPDFPermission && !contextualPDFPermission) {
    return null;
  }

  if (contextualPDFPermission || geoLookALikePermission) {
    return (
      <div className="reportingHeader__downloadButtons">
        {sonataPDFPermission && (
          <ReportingInsightsExporter
            buttonProps={{
              ...rest,
              className: 'reportingHeader__downloadButtonsButton',
              icon: 'fa-regular fa-file-pdf',
              shape: 'link',
              size: 'small',
              text: `${formatMessage({
                defaultMessage: 'Insights Report',
                id: 'reporting.header.buttons.sonataReport'
              })} ${hasReportingDownloadV2 ? 'V1' : ''}`,
              type: 'secondary'
            }}
            elementTargetId={elementTargetId}
            onTogglePDFLoading={onTogglePDFLoading}
          />
        )}
        {contextualPDFPermission ? (
          <ButtonUI
            className="reportingHeader__downloadButtonsButton"
            disabled={!contextualDiscoveryInsightButtonIsEnabled}
            icon="fa-regular fa-file-pdf"
            id="reportingHeader-downloadButtons-contextualReport"
            key="reportingHeader-downloadButtons-contextualReport"
            loading={contextualDiscoveryReportStore?.status === 'pending'}
            onClick={() => contextualDiscoveryInsightsReportModalRef?.current?.openModal()}
            shape="link"
            size="small"
            text={formatMessage({
              defaultMessage: 'Contextual Report',
              id: 'reporting.header.buttons.contextualReport'
            })}
            type="secondary"
          />
        ) : null}
        {geoLookALikePermission ? <GeoLookALikeButton /> : null}
        <ContextualDiscoveryInsightsReportModal
          cids={campaignIds}
          countries={campaignsCountries}
          currencyCountry={user.country}
          id="reportingContextualDiscoveryInsightsReportModal"
          key={contextualReportModalKey}
          metricsKpis={formattedMetricsKpis}
          modalRef={contextualDiscoveryInsightsReportModalRef}
          onReportDownloadToggled={handleReportDownloadToggled}
          onReportModalClosed={handleReportModalClosed}
          user={user}
          userRoles={userRoles}
        />
      </div>
    );
  }
  if (sonataPDFPermission && !contextualPDFPermission) {
    return (
      <ReportingInsightsExporter
        buttonProps={{ ...rest }}
        elementTargetId={elementTargetId}
        onTogglePDFLoading={onTogglePDFLoading}
      />
    );
  }
  return null;
}

export default DownloadPDFButton;
